<template>
    <div class="autocomplete">
        <input type="text" :id="formId"
               @input="value = $event.target.value"
               :placeholder="placeholder"
               autocomplete="off" :name="name"
               @keydown.down="onArrowDown"
               :value.sync="value"
               ref="autocompleteInput"
               @keydown.up="onArrowUp"
               @paste="onPaste"
               @beforepaste="onBeforePaste"
               @keydown.enter.prevent="onEnter" />
        <portal to="autocomplete" :order="portalOrder">
            <ul style="position: fixed; z-index: 10000"
                :style="{'max-height': maxHeight}"
                class="autocomplete-suggestions"
                v-show="isOpen"
                :id="autocompleteId"
            >
                <li
                    class="loading"
                    v-if="isLoading"
                >
                    Loading results...
                </li>
                <template v-else
                    v-for="(item, i) in results">
                    <li v-if="categoryIndexes.length > 0 && isNewCategory(item, i)"
                        class="autocomplete-group">
                        {{ itemCategory(item) }}
                    </li>
                    <li @click="setResult(item)"
                        :key="i"
                        class="autocomplete-suggestion"
                        :class="{ 'is-active': i === arrowCounter }">
                        <slot name="suggestionTemplate" v-bind:item="item">
                            {{ setValue(item) }}
                        </slot>
                    </li>
                </template>
            </ul>
        </portal>
    </div>
</template>

<script>
    export default {
        name: "Autocomplete",
        data () {
            return {
                results: [],
                isOpen: false,
                isLoading: false,
                arrowCounter: -1,
                autocompleteId: _.uniqueId('autocomplete-box'),
                portalOrder: parseInt(_.uniqueId()),
                categories: [],
            }
        },

        props: {
            dataSource: {
                type: String,
                required: true,
            },
            placeholder: {
                type: String,
                required: false,
                default: ''
            },
            name: {
                type: String,
                required: false,
                default: ''
            },
            threshold: {
                type: Number,
                required: false,
                default: 1
            },
            formId: {
                type: String,
                required: false,
                default: _.uniqueId('autocomplete')
            },
            subValue: {
                type: String,
                required: false,
                default: ''
            },
            categorySubValue: {
                type: String,
                required: false,
                default: ''
            },
            filters: {
                type: Object,
                required: false,
                default: () => ({})
            },
            maxHeight: {
                type: String,
                required: false,
                default: '250px'
            },
            disableAutocomplete: {
                type: Boolean,
                required: false,
                default: false
            },
            inputString: String
        },

        computed: {
            categoryIndexes() {
                if (this.categorySubValue === '')
                    return [];
                return this.categorySubValue.split('.');
            },
            value: {
                get() { return this.inputString },
                set(value) { this.$emit('update:inputString', value); }
            }
        },

        watch: {
            isOpen: function (newValue) {
                if (newValue) {
                    this.reflowAutocomplete();
                }
            },
            inputString(newValue) {
                this.$emit('input', newValue);
                let afterSemicolon = /([^;]+$)/;
                let trimmedValue = newValue.match(afterSemicolon);
                if (this.isLoading || this.disableAutocomplete)
                    return;
                if (!trimmedValue) {
                    return;
                }

                if (typeof trimmedValue[0] !== 'string') {
                    this.results = [];
                    return;
                }

                trimmedValue = trimmedValue[0].trimLeft();

                if (trimmedValue.length < this.threshold) {
                    this.results = [];
                    return;
                }
                this.isLoading = true;
                let baseParams = {
                    term: trimmedValue
                };
                let fullParams = {
                    ...this.filters,
                    ...baseParams
                };
                window.axios.get(this.dataSource, {
                    params: fullParams
                }).then(response => {
                    this.isOpen = response.data.length > 0;
                    this.results = response.data;
                    this.isLoading = false;
                }).catch(() => {
                    this.isOpen = false;
                    this.results = [];
                    this.isLoading = false;
                });
            }
        },

        methods: {
            onArrowDown: function () {
                if (this.arrowCounter + 1 < this.results.length) {
                    this.arrowCounter = this.arrowCounter + 1;
                }
            },
            onArrowUp: function () {
                if (this.arrowCounter > 0) {
                    this.arrowCounter = this.arrowCounter - 1;
                }
            },
            onEnter: function () {
                if (this.arrowCounter === -1) {
                    this.isOpen = false;
                    return;
                }
                this.value = this.setValue(this.results[this.arrowCounter]);
                this.isOpen = false;
                this.$emit('selected', this.results[this.arrowCounter]);
                this.arrowCounter = -1;
            },
            setResult: function (result) {
                this.value = this.setValue(result);
                this.isOpen = false;
                this.$emit('selected', result);
            },
            setValue: function (item) {
                let prefix = '';
                if (this.value.includes(';'))
                    prefix = this.value.match(/^(.*?)*;/)[0] + ' ';
                if (this.subValue.length === 0)
                    return prefix + item;
                return prefix + item[this.subValue];
            },
            handleClickOutside: function (event) {
                if (!this.$el.contains(event.target)) {
                    this.isOpen = false;
                    this.arrowCounter = -1;
                }
            },
            isNewCategory: function (item, index) {
                if (item === null)
                    return false;
                let currentCategory = this.itemCategory(item);
                if (currentCategory !== '') {
                    this.categories[index] = item.value.kind;
                    if (typeof this.categories[index - 1] !== 'undefined') {
                        return item.value.kind !== this.categories[index - 1];
                    }
                    return true;
                }
                return false;
            },
            itemCategory: function (item) {
                if (item === null)
                    return false;
                let currentCategory = item;
                this.categoryIndexes.forEach(function (index) {
                    currentCategory = currentCategory[index];
                });
                return currentCategory
            },

            reflowAutocomplete: function () {
                let component = this;
                this.$nextTick(function () {
                    let $input = $('#' + component.formId);
                    let inputOffset = $input.offset();
                    let inputHeight = $input.outerHeight();
                    let inputWidth = $input.outerWidth();
                    this.$nextTick(function () {
                        let $autocomplete = $('#' + component.autocompleteId);
                        $autocomplete.outerWidth(inputWidth);
                        $autocomplete.offset({
                            top: inputOffset.top + inputHeight,
                            left: inputOffset.left
                        });
                    });
                });
            },

            onPaste: function (event) {
                this.$emit('paste', event);
                return false;
            },

            onBeforePaste: function (event) {
                this.$emit('beforepaste', event);
                return false;
            }
        },
        mounted() {
            document.addEventListener('click', this.handleClickOutside);
            let component = this;
            this.$nextTick(function () {
                let $input = $('#' + component.formId);
                let inputOffset = $input.offset();
                let inputHeight = $input.outerHeight();
                let inputWidth = $input.outerWidth();
                this.$nextTick(function () {
                    let $autocomplete = $('#' + component.autocompleteId);
                    $autocomplete.outerWidth(inputWidth);
                    $autocomplete.offset({
                        top: inputOffset.top + inputHeight,
                        left: inputOffset.left
                    });
                });
            })
        },
        destroyed() {
            document.removeEventListener('click', this.handleClickOutside);
        }
    }
</script>

<style scoped>
    .autocomplete-suggestion.is-active,
    .autocomplete-suggestion:hover {
        background-color: #92b2f1;
        color: white;
    }
</style>
